import { RefObject, Suspense } from 'react';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { useLocale } from 'next-intl';

import StripePayment, {
  StripePaymentRef,
} from 'components/payment/components/StripePaymentContainer/StripePayment';
import {
  PaymentElementFutureUsageType,
  PaymentElementLanguageType,
  PaymentElementModeType,
  StripeThemeType,
} from 'types/checkout';
import { OptionalProductAdjustQuantityType } from 'types/productPackage';
import Loading from 'ui/Loading';

const StripePaymentContainer = ({
  price,
  stripePublishableKey,
  packageId,
  checkoutCompletedUrl,
  reference,
  fullName,
  email,
  slug,
  selectedOptionalProducts,
  handleSetErrorMessage,
  handleSetStripeFromState,
  handleSetPaymentLoading,
  stripePaymentRef,
}: {
  price: number;
  stripePublishableKey: string;
  packageId: number;
  checkoutCompletedUrl: string;
  reference: string;
  fullName: string;
  email: string;
  slug: string;
  selectedOptionalProducts: OptionalProductAdjustQuantityType[];
  handleSetErrorMessage: (message: string) => void;
  handleSetStripeFromState: (stare: 'ready' | 'error' | 'loading') => void;
  handleSetPaymentLoading: (loading: boolean) => void;
  stripePaymentRef: RefObject<StripePaymentRef>;
}) => {
  const locale = useLocale();

  let stripePromise = null;

  if (stripePublishableKey) {
    stripePromise = loadStripe(stripePublishableKey);
  }

  const stripeElementOptions = {
    mode: 'payment' as PaymentElementModeType,
    amount: price * 100, // Stripe expects the price in cents
    currency: 'nok',
    locale: locale as PaymentElementLanguageType,
    fontFamily: 'Roboto, sans-serif',
    appearance: {
      theme: 'stripe' as StripeThemeType,
      rules: {
        '.Input': {
          marginTop: '4px',
        },
        '.Label': {
          marginTop: '4px',
          fontWeight: '500',
          lineHeight: '28px',
        },
      },
    },
    setupFutureUsage: 'off_session' as PaymentElementFutureUsageType,
    paymentMethodTypes: ['card'],
  };

  return (
    <>
      {stripeElementOptions.amount > 0 && (
        <Suspense fallback={<Loading />}>
          <Elements
            options={stripeElementOptions as unknown as StripeElementsOptions}
            stripe={stripePromise}
          >
            <StripePayment
              ref={stripePaymentRef}
              packageId={packageId}
              checkoutCompletedUrl={checkoutCompletedUrl}
              reference={reference}
              fullName={fullName}
              email={email}
              slug={slug}
              selectedOptionalProducts={selectedOptionalProducts}
              handleSetErrorMessage={handleSetErrorMessage}
              handleSetStripeFromState={handleSetStripeFromState}
              handleSetPaymentLoading={handleSetPaymentLoading}
            />
          </Elements>
        </Suspense>
      )}
    </>
  );
};

export default StripePaymentContainer;
